import React from 'react';
import {
  ArrowRightIcon,
  ShieldCheckIcon,
  ArrowTopRightOnSquareIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { keynetStartUrl } from '../services/session';

/**
 * Landing screen shown when a user hits KeyScan without an active
 * KeyNet session. Under SSO, direct KeyScan login is disabled — every
 * user enters through KeyNet, which mints the KeyScan session on the
 * way in. This page tells the user what to do when they arrive with no
 * session, and provides a jump to their KeyNet portal.
 *
 * For the NASU demo this page stands in for the future auto-redirect:
 * eventually the handshake replaces this landing entirely for anyone
 * with an active KeyNet cookie.
 */
const KeyNetRedirectPage: React.FC = () => {
  // Delegate to the backend's /auth/keynet/start, same path the login page
  // uses, so there is one source of truth for KeyNet URL config. The
  // backend reads KEYNET_START_URL from its env and redirects.
  const target = keynetStartUrl('/');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-50 dark:from-gray-900 dark:via-gray-900 dark:to-brand-900/20 flex flex-col">
      {/* Header bar */}
      <header className="w-full py-4 px-6 md:px-12 flex items-center justify-between border-b border-gray-200/60 dark:border-gray-700/60 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-brand-600 flex items-center justify-center text-white font-bold text-sm">
            KS
          </div>
          <span className="text-lg font-semibold text-gray-900 dark:text-white">
            KeyScan <span className="text-gray-400 dark:text-gray-500 font-normal">AP</span>
          </span>
        </div>
        <a
          href="https://www.keyinfosys.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center gap-1"
        >
          Keystone
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
        </a>
      </header>

      {/* Hero section */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-2xl">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200/70 dark:border-gray-700/70 overflow-hidden">
            {/* Top band */}
            <div className="px-8 md:px-12 pt-10 pb-8 border-b border-gray-100 dark:border-gray-700">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-800 dark:text-brand-200 text-xs font-semibold uppercase tracking-wider mb-4">
                <ShieldCheckIcon className="h-4 w-4" />
                Single sign-on via KeyNet
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white leading-tight">
                Sign in through KeyNet to continue
              </h1>
              <p className="mt-4 text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                KeyScan uses your KeyNet account to sign you in. Open your
                organization's KeyNet portal and click the KeyScan button
                to start reviewing invoices.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <a
                  href={target}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white text-sm font-semibold shadow-sm transition"
                >
                  Go to KeyNet
                  <ArrowRightIcon className="h-4 w-4" />
                </a>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Takes you to your KeyNet portal to sign in.
                </span>
              </div>
            </div>

            {/* Steps */}
            <div className="px-8 md:px-12 py-8 bg-gray-50/70 dark:bg-gray-800/60">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-4">
                How to sign in
              </h2>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 h-6 w-6 rounded-full bg-brand-600 text-white text-xs font-bold flex items-center justify-center">1</span>
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      Open KeyNet
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Log in with the credentials you already use for
                      Keystone.
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 h-6 w-6 rounded-full bg-brand-600 text-white text-xs font-bold flex items-center justify-center">2</span>
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      Find KeyScan AP in the menu
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Under the Accounts Payable dropdown, click the
                      KeyScan AP option.
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 h-6 w-6 rounded-full bg-brand-600 text-white text-xs font-bold flex items-center justify-center">3</span>
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      Land on KeyScan
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      A new tab opens and you're signed in automatically.
                      Start uploading invoices.
                    </div>
                  </div>
                </li>
              </ol>
            </div>
          </div>

          {/* FAQ / support strip */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70">
              <div className="flex items-center gap-2 mb-2">
                <QuestionMarkCircleIcon className="h-5 w-5 text-gray-400" />
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  Don't see the KeyScan option in KeyNet?
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                You may not have the AP or Voucher Requisition role assigned.
                Contact your KeyNet administrator to request access.
              </p>
            </div>
            <div className="p-5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheckIcon className="h-5 w-5 text-gray-400" />
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  Why sign in through KeyNet?
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                One login for the whole Keystone suite, one place to manage
                users and roles, and no separate password for KeyScan to
                remember.
              </p>
            </div>
          </div>

          {/* Footer note */}
          <p className="mt-8 text-center text-xs text-gray-400 dark:text-gray-500">
            KeyScan is part of the Keystone product family from Keystone
            Information Systems.
          </p>
        </div>
      </main>
    </div>
  );
};

export default KeyNetRedirectPage;
